"use client";

import { ChevronUpDownIcon, ChevronUpIcon, ChevronDownIcon } from "@heroicons/react/16/solid";
import { clsx } from "clsx";
import type React from "react";
import { createContext, useContext, useState } from "react";
import { Link, LinkProps } from "./link";

type TableContextValue = {
    bleed: boolean;
    dense: boolean;
    grid: boolean;
    striped: boolean;
    stickyHeader: boolean;
    columnVisibility: Record<string, boolean>;
};

const TableContext = createContext<TableContextValue>({
    bleed: false,
    dense: false,
    grid: false,
    striped: false,
    stickyHeader: false,
    columnVisibility: {},
});

export function Table({
    bleed = false,
    dense = false,
    grid = false,
    striped = false,
    stickyHeader = false,
    columnVisibility = {},
    className,
    children,
    ...props
}: {
    bleed?: boolean;
    dense?: boolean;
    grid?: boolean;
    striped?: boolean;
    stickyHeader?: boolean;
    /** Map of column `name` → visibility. Missing entries default to visible. */
    columnVisibility?: Record<string, boolean>;
} & React.ComponentPropsWithoutRef<"div">) {
    return (
        <TableContext.Provider
            value={
                {
                    bleed,
                    dense,
                    grid,
                    striped,
                    stickyHeader,
                    columnVisibility,
                } as TableContextValue
            }
        >
            <div className="flow-root">
                <div {...props} className={clsx(className, "-mx-(--gutter) overflow-x-auto whitespace-nowrap")}>
                    <div className={clsx("inline-block min-w-full align-middle", !bleed && "sm:px-(--gutter)")}>
                        <table className="min-w-full text-left text-sm/6">{children}</table>
                    </div>
                </div>
            </div>
        </TableContext.Provider>
    );
}

export function TableHead({ className, ...props }: React.ComponentPropsWithoutRef<"thead">) {
    const { stickyHeader } = useContext(TableContext);
    return (
        <thead
            {...props}
            className={clsx(
                className,
                "text-zinc-500 dark:text-zinc-400",
                stickyHeader && "sticky top-0 z-10 bg-(--surface-card) backdrop-blur-sm",
            )}
        />
    );
}

export function TableBody({ className, ...props }: React.ComponentPropsWithoutRef<"tbody">) {
    return <tbody {...props} className={clsx(className, "text-zinc-700 dark:text-zinc-200")} />;
}

export function TableRow({
    href,
    params,
    search,
    target,
    title,
    className,
    ...props
}: {
    href?: LinkProps["href"];
    params?: LinkProps["params"];
    search?: LinkProps["search"];
    target?: string;
    title?: string;
} & React.ComponentPropsWithoutRef<"tr">) {
    const { striped } = useContext(TableContext);

    return (
        <TableRowContext.Provider
            value={{ href, params, search, target, title } as React.ContextType<typeof TableRowContext>}
        >
            <tr
                {...props}
                className={clsx(
                    className,
                    href &&
                        "focus-within:outline-2 focus-within:-outline-offset-2 focus-within:outline-(--color-brand-500) has-[[data-row-link][data-focus]]:outline-2 has-[[data-row-link][data-focus]]:-outline-offset-2 has-[[data-row-link][data-focus]]:outline-(--color-brand-500) dark:focus-within:bg-white/2.5",
                    striped && "even:bg-zinc-950/2.5 dark:even:bg-white/2.5",
                    href && striped && "hover:bg-zinc-950/5 dark:hover:bg-white/5",
                    href && !striped && "hover:bg-zinc-950/2.5 dark:hover:bg-white/2.5",
                )}
            />
        </TableRowContext.Provider>
    );
}

const TableRowContext = createContext<{
    href?: LinkProps["href"];
    params?: LinkProps["params"];
    search?: LinkProps["search"];
    target?: string;
    title?: string;
}>({
    href: undefined,
    target: undefined,
    title: undefined,
});

type SortDirection = "asc" | "desc" | undefined;

export function TableHeader({
    name,
    sortable,
    direction,
    onSort,
    className,
    children,
    ...props
}: {
    /** Column identifier — used by `Table.columnVisibility` to hide this column. */
    name?: string;
    sortable?: boolean;
    direction?: SortDirection;
    onSort?: React.MouseEventHandler<HTMLButtonElement>;
} & React.ComponentPropsWithoutRef<"th">) {
    const { bleed, grid, columnVisibility } = useContext(TableContext);

    if (name && !columnVisibility[name]) return null;

    const headerClass = clsx(
        className,
        "border-b border-b-zinc-950/10 px-4 py-2 font-medium first:pl-(--gutter,--spacing(2)) last:pr-(--gutter,--spacing(2)) dark:border-b-white/10",
        grid && "border-l border-l-zinc-950/5 first:border-l-0 dark:border-l-white/5",
        !bleed && "sm:first:pl-1 sm:last:pr-1",
    );

    if (sortable) {
        return (
            <th
                {...props}
                className={headerClass}
                aria-sort={direction === "asc" ? "ascending" : direction === "desc" ? "descending" : undefined}
            >
                <button
                    type="button"
                    onClick={onSort}
                    className="-mx-1 -my-0.5 inline-flex items-center gap-1 rounded-(--radius-control) px-1 py-0.5 hover:text-zinc-700 focus:outline-2 focus:outline-offset-2 focus:outline-(--color-brand-500) dark:hover:text-zinc-200"
                >
                    {children}
                    {direction === "asc" ? (
                        <ChevronUpIcon className="size-3.5" />
                    ) : direction === "desc" ? (
                        <ChevronDownIcon className="size-3.5" />
                    ) : (
                        <ChevronUpDownIcon className="size-3.5 opacity-50" />
                    )}
                </button>
            </th>
        );
    }

    return (
        <th {...props} className={headerClass}>
            {children}
        </th>
    );
}

export function TableCell({
    name,
    className,
    children,
    ...props
}: {
    /** Column identifier — used by `Table.columnVisibility` to hide this column. */
    name?: string;
} & React.ComponentPropsWithoutRef<"td">) {
    const { bleed, dense, grid, striped, columnVisibility } = useContext(TableContext);
    const { href, search, params, target, title } = useContext(TableRowContext);
    const [cellRef, setCellRef] = useState<HTMLElement | null>(null);

    if (name && !columnVisibility[name]) return null;

    return (
        <td
            ref={href ? setCellRef : undefined}
            {...props}
            className={clsx(
                className,
                "relative px-4 first:pl-(--gutter,--spacing(2)) last:pr-(--gutter,--spacing(2))",
                !striped && "border-b border-zinc-950/5 dark:border-white/5",
                grid && "border-l border-l-zinc-950/5 first:border-l-0 dark:border-l-white/5",
                dense ? "py-2.5" : "py-4",
                !bleed && "sm:first:pl-1 sm:last:pr-1",
            )}
        >
            {href && (
                <Link
                    data-row-link
                    href={href}
                    params={params}
                    search={search}
                    target={target}
                    aria-label={title}
                    //tabIndex={cellRef?.previousElementSibling === null ? 0 : -1}
                    className="absolute inset-0 focus:outline-hidden"
                />
            )}
            {children}
        </td>
    );
}
